import { ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import * as Dialog from 'components/dialog';
import { useForceRender } from 'customHooks/useForceRender';

interface ExportDataDialogProps extends Dialog.DialogProps {
    label: string;
    onSubmit: Function;
    dropdown?: ReactNode;
}

const ExportDataDialog = ({
    label,
    onSubmit,
    dropdown = null,
    children,
    ...rest
}: ExportDataDialogProps) => {
    const { key, forceRender } = useForceRender();
    const toast = useToast();

    const { mutate: submit, isPending: isLoading } = useMutation({
        mutationFn: () => {
            return onSubmit();
        },
        onSuccess: () => {
            toast.success(
                <>
                    <span className="text-semibold">Success!</span> We'll send you an email when
                    your export is ready.
                </>,
            );

            forceRender();
        },
        onError: () => {
            toast.error(
                <>
                    <span className="text-semibold">Whoops.</span> Something went wrong. Please try
                    again.
                </>,
            );
        },
    });

    return (
        <Dialog.Root key={key} {...rest}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content>
                    <div className="flex flex-col gap-3">
                        <p className="text-sm font-medium text-gray-500">{label}</p>
                        <Dialog.Close className="p-2" />
                        <Dialog.Title className="text-xl font-medium">Export</Dialog.Title>
                        <Dialog.Description className="text-sm font-thin text-black">
                            Click below to start your export. We'll email you when it's complete.
                        </Dialog.Description>
                        <div className="pt-2">
                            {dropdown}
                            <Button variant="solid" onClick={() => submit()} disabled={isLoading}>
                                Export
                            </Button>
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const ExportDataDialogTrigger = Dialog.Trigger;
const Root = ExportDataDialog;

const Trigger = ExportDataDialogTrigger;

export { ExportDataDialog, ExportDataDialogTrigger, Root, Trigger };
